import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { actions as modalActions } from '~publish/legacy/modals/reducer'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'
import { useAccount } from '~publish/contexts/AccountContext'
import AppActionCreators from '../../action-creators/AppActionCreators'
import {
  ButtonsQueuingTypesMap,
  PaywallButtonTypes,
  SaveButtonTypes,
} from '../../AppConstants'
import { UserDataContext } from '../../hooks/userDataContext'
import DateTimeSlotPicker from '../DateTimeSlotPicker'
import { ButtonStyled } from './style'

const ScheduleTimeWrapper = styled.div`
  position: absolute;
  bottom: 0px;
  right: 100%;
  left: auto;
  margin-right: 10px;
  z-index: 20;
  padding: 5px 0;
  background: white;
  border: 1px solid #d5dce3;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-2);
`

const DraftSaveButtonWrapper = styled.div`
  position: relative;
`

interface ScheduleTimeProps {
  timezone?: string
  draftMode: boolean
  buttonAction: keyof typeof SaveButtonTypes
  onClickOutside: () => void
}

const ScheduleTime = ({
  timezone,
  draftMode,
  buttonAction,
  onClickOutside,
}: ScheduleTimeProps): JSX.Element => {
  const scheduleTimeRef = useRef<HTMLDivElement>(null)
  const { uses24hTime, weekStartsMonday } = useContext(UserDataContext)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        scheduleTimeRef.current &&
        !scheduleTimeRef.current?.contains(event.target as Node)
      ) {
        onClickOutside()
      }
    }
    document.addEventListener('mousedown', handleClickOutside, true)

    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [])

  const onDateTimeSlotPickerSubmit = (timestamp: number): void => {
    const queuingType = ButtonsQueuingTypesMap.get(buttonAction)

    AppActionCreators.saveDrafts(queuingType, {
      customScheduleTime: timestamp,
      shouldSkipEmptyTextAlert: false,
      saveDraftsFromQueue: draftMode,
      hasCampaignsToTagsFeature: true,
    })
    onClickOutside()
  }

  return (
    <ScheduleTimeWrapper
      ref={scheduleTimeRef}
      data-testid="schedule-time-container"
    >
      <DateTimeSlotPicker
        onSubmit={onDateTimeSlotPickerSubmit}
        shouldUse24hTime={!!uses24hTime}
        timezone={timezone}
        weekStartsMonday={!!weekStartsMonday}
        submitButtonCopy={draftMode ? 'Add Draft' : 'Schedule'}
        showDraftScheduleNotice={draftMode}
      />
    </ScheduleTimeWrapper>
  )
}

type DraftSaveItem = {
  id: string
  title: string
  icon?: JSX.Element
  iconEnd?: boolean
}

interface DraftSaveButtonsProps {
  isDisabled?: boolean
  draftSaveItems: DraftSaveItem[]
  timezone?: string
}

const DraftSaveButtons = ({
  isDisabled = false,
  timezone,
  draftSaveItems,
}: DraftSaveButtonsProps): JSX.Element => {
  const { account } = useAccount()
  const [showScheduleDraftTime, setShowScheduleDraftTime] = useState(false)
  const dispatch = useDispatch()
  const draftButtonRef = useRef<HTMLDivElement>(null)
  const [popoverPosition, setPopoverPosition] = useState<'top' | 'bottom'>(
    'bottom',
  )

  const handleScheduleDraftAction = (): void => {
    setShowScheduleDraftTime(true)
  }

  const commonTrackingProps =
    account?.currentOrganization?.commonTrackingProperties || null

  const handleDraftAction = (buttonType: {
    id: keyof typeof SaveButtonTypes | keyof typeof PaywallButtonTypes
  }): void => {
    if (buttonType.id === SaveButtonTypes.SCHEDULE_DRAFT) {
      handleScheduleDraftAction()
    } else if (buttonType.id === PaywallButtonTypes.REQUEST_APPROVAL_PAYWALL) {
      BufferTracker.cTAClicked({
        organizationId: account?.currentOrganization?.id || '',
        cta: 'publish-composer-requestApproval-upsell-1',
        upgradePathName: 'approvals-upgrade',
        product: 'publish',
        clientName: 'publishWeb',
        ...commonTrackingProps,
      })
      dispatch(modalActions.showTrialCollaborationModal())
    } else {
      const queuingType = ButtonsQueuingTypesMap.get(buttonType.id)

      AppActionCreators.saveDrafts(queuingType, {
        shouldSkipEmptyTextAlert: false,
        saveDraftsFromQueue: true,
        hasCampaignsToTagsFeature: true,
      })
    }
  }

  const handleOpenDropdown = (): void => {
    if (draftButtonRef.current) {
      const popover =
        draftButtonRef.current.parentElement?.querySelector('ul')?.parentElement
      if (popover) {
        const buttonRect = draftButtonRef.current.getBoundingClientRect()
        const popoverRect = popover.getBoundingClientRect()

        const isOutsideWindow =
          buttonRect.bottom + popoverRect.height > window.innerHeight
        setPopoverPosition(isOutsideWindow ? 'top' : 'bottom')
      }
    }
  }

  const handleClickOutsideScheduleTime = (): void => {
    setShowScheduleDraftTime(false)
  }

  const [firstItem, ...restOfItems] = draftSaveItems

  return (
    <DraftSaveButtonWrapper>
      {showScheduleDraftTime && (
        <ScheduleTime
          timezone={timezone}
          draftMode
          buttonAction={SaveButtonTypes.SCHEDULE_DRAFT}
          onClickOutside={handleClickOutsideScheduleTime}
        />
      )}
      <div ref={draftButtonRef}>
        <ButtonStyled
          data-testid="draft-save-buttons"
          disabled={isDisabled}
          onSelectClick={handleDraftAction}
          onClick={(): void =>
            handleDraftAction({ id: SaveButtonTypes.ADD_TO_DRAFTS })
          }
          onOpen={handleOpenDropdown}
          type="secondary"
          isSplit={!!restOfItems.length}
          items={restOfItems}
          label={firstItem.title}
          selectPosition={popoverPosition}
        />
      </div>
    </DraftSaveButtonWrapper>
  )
}

export { DraftSaveButtons, ScheduleTime }
